import { Employee, Company } from '../../types';
import { sentenceCasePtBr, canonicalizeEmployeeRole } from '../../utils/textFormat';
import {
  parseDbRows,
  rhEmployeeRowSchema,
  rhAbsenceRowSchema,
  rhSituationRowSchema,
  rhDelayRowSchema,
  rhEarlyDepartureRowSchema,
  type RhEmployeeRow,
} from '../rhDataSchemas';
import { EMPLOYEE_FETCH_PAGE_SIZE } from '../../hooks/appDefaults';
import { toAppEmployee } from './mappers';
import type { DbClient } from './types';

/** Campos de auditoria opcionais ao persistir ocorrências do colaborador. */
type RecordAuditInput = {
  createdBy?: string;
  createdAt?: string;
};

/** Regex para validar UUID v4. */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Monta linha de insert preservando metadados de quem registrou e quando.
 * Sempre garante um UUID válido em `id` — registros novos (id temporário como
 * Date.now()) recebem um UUID gerado no cliente para evitar inconsistência de
 * colunas no INSERT em lote no servidor (null em PK causa erro 500).
 * @param record - Dados da ocorrência com auditoria opcional
 * @param employeeId - UUID do colaborador
 * @param fields - Demais colunas específicas da tabela
 * @returns Objeto pronto para INSERT via API
 */
function buildEmployeeRecordInsertRow<T extends Record<string, unknown>>(
  record: { id: string } & RecordAuditInput,
  employeeId: string,
  fields: T,
): T & { id: string; employee_id: string; created_by?: string; created_at?: string } {
  const row: T & { id: string; employee_id: string; created_by?: string; created_at?: string } = {
    ...fields,
    employee_id: employeeId,
    id: UUID_REGEX.test(record.id) ? record.id : crypto.randomUUID(),
  };
  if (record.createdBy) row.created_by = record.createdBy;
  if (record.createdAt) row.created_at = record.createdAt;
  return row;
}

/**
 * Normaliza data-only (YYYY-MM-DD) para colunas DATE do Postgres.
 * @param value - Data em YYYY-MM-DD ou ISO
 * @returns Data sem componente de hora
 */
function normalizeDateOnly(value: string): string {
  return value.includes('T') ? value.split('T')[0] : value;
}

/**
 * Carrega todas as linhas de rh_employees da empresa em páginas (limit/offset).
 * @param sb - Cliente da API Postgres
 * @param companyId - UUID da empresa
 */
async function fetchAllEmployeeRows(sb: DbClient, companyId: string): Promise<RhEmployeeRow[]> {
  const all: RhEmployeeRow[] = [];
  let offset = 0;
  let total = Infinity;

  while (offset < total) {
    const page = await sb
      .from('rh_employees')
      .select('*')
      .eq('company_id', companyId)
      .order('sort_order')
      .limit(EMPLOYEE_FETCH_PAGE_SIZE)
      .offset(offset)
      .count('exact');

    if (page.error) throw page.error;

    const rows = parseDbRows(rhEmployeeRowSchema, page.data ?? [], 'rh_employees');
    all.push(...rows);
    total = page.total ?? rows.length;
    offset += rows.length;
    if (rows.length === 0) break;
  }

  return all;
}

/**
 * Carrega colaboradores agrupados por coluna Kanban.
 * @param sb - Cliente API
 * @param companyId - UUID da empresa
 */
export async function loadEmployees(
  sb: DbClient,
  companyId: string,
): Promise<{ items: Record<string, Employee[]>; columnKeyMap: Record<string, string> }> {
  const [colRes, absRes, sitRes, delayRes, earlyDepRes] = await Promise.all([
    sb.from('rh_columns').select('id, column_key').eq('company_id', companyId),
    sb.fetchAbsencesByCompany(companyId),
    sb.fetchSituationsByCompany(companyId),
    sb.fetchDelaysByCompany(companyId),
    sb.fetchEarlyDeparturesByCompany(companyId),
  ]);

  if (colRes.error) throw colRes.error;
  if (absRes.error) throw absRes.error;
  if (sitRes.error) throw sitRes.error;
  if (delayRes.error) throw delayRes.error;
  if (earlyDepRes.error) throw earlyDepRes.error;

  const empRows = await fetchAllEmployeeRows(sb, companyId);
  const absRows = parseDbRows(rhAbsenceRowSchema, absRes.data ?? [], 'rh_absences');
  const sitRows = parseDbRows(rhSituationRowSchema, sitRes.data ?? [], 'rh_situations');
  const delayRows = parseDbRows(rhDelayRowSchema, delayRes.data ?? [], 'rh_delays');
  const earlyDepRows = parseDbRows(
    rhEarlyDepartureRowSchema,
    earlyDepRes.data ?? [],
    'rh_early_departures',
  );

  const colRows = (colRes.data ?? []) as { id: string; column_key: string }[];

  const uuidToKey: Record<string, string> = {};
  const columnKeyMap: Record<string, string> = {};
  colRows.forEach((c) => {
    uuidToKey[c.id] = c.column_key;
    columnKeyMap[c.column_key] = c.id;
  });

  const items: Record<string, Employee[]> = {};
  colRows.forEach((c) => {
    items[c.column_key] = [];
  });

  empRows.forEach((row) => {
    const key = uuidToKey[row.column_id];
    if (key) {
      items[key] = items[key] ?? [];
      items[key].push(toAppEmployee(row, absRows, sitRows, delayRows, earlyDepRows));
    }
  });

  return { items, columnKeyMap };
}

/**
 * Insere ou atualiza colaborador em rh_employees.
 * @returns UUID persistido
 */
export async function upsertEmployee(
  sb: DbClient,
  employee: Employee,
  columnKey: string,
  companyId: string,
  columnKeyMap: Record<string, string>,
): Promise<string> {
  const columnId = columnKeyMap[columnKey];
  if (!columnId) throw new Error(`Column key "${columnKey}" não encontrado no mapa`);

  const payload: Partial<RhEmployeeRow> & { company_id: string; column_id: string } = {
    company_id: companyId,
    column_id: columnId,
    employee_code: employee.code || null,
    name: employee.name,
    role: canonicalizeEmployeeRole(employee.role),
    status: employee.status,
    probation_period: null,
    birth_date: employee.birthDate || null,
    hiring_date: employee.hiringDate || null,
    whatsapp: employee.whatsapp || null,
    last_birthday_msg_year: employee.lastBirthdayMsgYear || null,
    avatar: employee.avatar || '',
    cost_center_code: employee.costCenterCode || null,
    cost_center_description:
      employee.costCenterDescription != null && employee.costCenterDescription !== ''
        ? sentenceCasePtBr(employee.costCenterDescription)
        : null,
  };

  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(employee.id);
  if (isUuid) {
    const { data, error } = await sb
      .from('rh_employees')
      .upsert({ id: employee.id, ...payload }, { onConflict: 'id' })
      .select('id')
      .single();
    if (error) throw error;
    return data.id;
  }

  const { data, error } = await sb.from('rh_employees').insert(payload).select('id').single();
  if (error) throw error;
  return data.id;
}

/** Move colaborador para outra coluna Kanban. */
export async function moveEmployee(
  sb: DbClient,
  employeeId: string,
  newColumnKey: string,
  columnKeyMap: Record<string, string>,
): Promise<void> {
  const newColumnId = columnKeyMap[newColumnKey];
  if (!newColumnId) return;
  const { error } = await sb.from('rh_employees').update({ column_id: newColumnId }).eq('id', employeeId);
  if (error) throw error;
}

/** Remove colaborador (cascade em absences/situations). */
export async function deleteEmployee(sb: DbClient, employeeId: string): Promise<void> {
  const { error } = await sb.from('rh_employees').delete().eq('id', employeeId);
  if (error) throw error;
}

/**
 * Substitui faltas do colaborador de forma atômica (DELETE + INSERT em transação única).
 * Evita perda de dados caso o INSERT falhe após um DELETE bem-sucedido.
 */
export async function upsertAbsences(
  sb: DbClient,
  employeeId: string,
  absences: ({ id: string; date: string; reason: string } & RecordAuditInput)[],
): Promise<void> {
  const rows = absences.map((a) =>
    buildEmployeeRecordInsertRow(a, employeeId, {
      date: normalizeDateOnly(a.date),
      reason: a.reason,
    }),
  );
  const { error } = await sb.replaceEmployeeRecords('rh_absences', employeeId, rows);
  if (error) throw error;
}

/**
 * Substitui situações do colaborador de forma atômica (DELETE + INSERT em transação única).
 */
export async function upsertSituations(
  sb: DbClient,
  employeeId: string,
  situations: ({ id: string; date: string; description: string } & RecordAuditInput)[],
): Promise<void> {
  const rows = situations.map((s) =>
    buildEmployeeRecordInsertRow(s, employeeId, {
      date: normalizeDateOnly(s.date),
      description: s.description.trim(),
    }),
  );
  const { error } = await sb.replaceEmployeeRecords('rh_situations', employeeId, rows);
  if (error) throw error;
}

/**
 * Substitui atrasos do colaborador de forma atômica (DELETE + INSERT em transação única).
 */
export async function upsertDelays(
  sb: DbClient,
  employeeId: string,
  delays: ({ id: string; date: string; reason: string } & RecordAuditInput)[],
): Promise<void> {
  const rows = delays.map((d) =>
    buildEmployeeRecordInsertRow(d, employeeId, {
      date: normalizeDateOnly(d.date),
      reason: d.reason,
    }),
  );
  const { error } = await sb.replaceEmployeeRecords('rh_delays', employeeId, rows);
  if (error) throw error;
}

/**
 * Substitui saídas antecipadas do colaborador de forma atômica (DELETE + INSERT em transação única).
 */
export async function upsertEarlyDepartures(
  sb: DbClient,
  employeeId: string,
  earlyDepartures: ({ id: string; date: string; reason: string } & RecordAuditInput)[],
): Promise<void> {
  const rows = earlyDepartures.map((ed) =>
    buildEmployeeRecordInsertRow(ed, employeeId, {
      date: normalizeDateOnly(ed.date),
      reason: ed.reason,
    }),
  );
  const { error } = await sb.replaceEmployeeRecords('rh_early_departures', employeeId, rows);
  if (error) throw error;
}

/**
 * Cria colunas, funções e centros de custo iniciais no servidor (defaults fora do bundle).
 * @param sb - Cliente API
 * @param _company - Empresa alvo (nome resolvido no servidor via company_id)
 * @param companyId - UUID da empresa
 */
export async function seedCompanyStructure(
  sb: DbClient,
  _company: Company,
  companyId: string,
): Promise<void> {
  const { error } = await sb.seedCompanyStructure(companyId);
  if (error) throw error;
}

/** @deprecated Use seedCompanyStructure; colaboradores via npm run seed:employees */
export async function seedCompanyData(
  sb: DbClient,
  company: Company,
  companyId: string,
): Promise<void> {
  await seedCompanyStructure(sb, company, companyId);
}
